/**
 * Niveau d'alerte d'un quota.
 *
 * Extrait du composant pour être testable et partagé. Les seuils doivent
 * rester alignés sur ceux du cron d'alerte email (apps/api/src/lib/cron.ts) :
 * un utilisateur prévenu par email à 80 % doit voir la barre changer de
 * couleur au même moment, sans quoi les deux signaux se contredisent.
 */
export type QuotaLevel = 'ok' | 'warning' | 'danger';

export interface QuotaState {
  readonly percent: number;
  readonly level: QuotaLevel;
}

export function quotaState(used: number, limit: number): QuotaState {
  // Une limite nulle ou négative n'a pas de sens : on considère le quota
  // comme non contraignant plutôt que de diviser par zéro.
  if (limit <= 0) {
    return { percent: 0, level: 'ok' };
  }

  const ratio = used / limit;

  // Le niveau se calcule sur le ratio BRUT, jamais sur le pourcentage arrondi.
  //
  // À 799/1000, l'arrondi donne 80 % : la barre passerait à l'orange alors que
  // le cron — qui compare `ratio >= 0.8` — n'envoie aucun email. L'utilisateur
  // verrait une alerte visuelle sans explication, exactement la désynchro-
  // nisation que ce module est censé empêcher.
  const level: QuotaLevel = ratio >= 0.95 ? 'danger' : ratio >= 0.8 ? 'warning' : 'ok';

  return { percent: Math.min(100, Math.round(ratio * 100)), level };
}
